import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  message,
  Modal,
  Input,
} from 'antd';
import PropTypes from 'prop-types';
import {
  DoubleRightOutlined,
  RightOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import NumericInput from 'components/NumericInput';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getTiposDeCaracteres, getCatalogosDeValor } from 'api/configuracion';
import { getSegmentosDeFolios, patchSegmentosDeFolios, postSegmentoDeFolio } from 'api/configuracion/segmentosDeFolios';
import { getPeriodos } from 'api/recaudacion';
import { onError } from 'utils/handlers';
import { toUpper } from 'utils/normalizers';

export const model = 'segmentodefolio';
export const catalogsModels = ['tipodecaracter', 'periodo', 'catalogodevalor'];

const formatter = (item) => ({
  ...item,
  modificable: false,
  deletable: false,
  key: item.id,
});

const sorter = (a, b) => a.posicion.toString().localeCompare(b.posicion);

// const segmentosDefault = [{
//   tipo_de_segmento: 2,
//   catalogo_de_valor: 1,
//   longitud_del_folio: 2,
//   folio_siguiente: null,
//   key: 1,
//   posicion: 1,
//   modificable: true,
//   deletable: false,
// }];

const SegmentosFoliador = ({
  isMutable: _isMutable,
  setSegmentos,
  idConfiguracion,
  permission,
  hasAllViewPermission,
}) => {
  const allowAdd = _isMutable && permission.add;
  const isMutable = _isMutable && permission.change;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [tiposCaracteres, setTiposCaracteres] = useState([]);
  const [valores, setValores] = useState([]);
  const [filterdValores, setFilterdValores] = useState([]);
  const [consecutivos, setConsecutivos] = useState([]);
  const [isConsecutivo, setIsConsecutivo] = useState(false);
  const [isFijo, setIsFijo] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    if (idConfiguracion) {
      const segmentos = await getSegmentosDeFolios(idConfiguracion);
      const formattedData = segmentos.map(formatter).sort(sorter);
      setSegmentos(formattedData);
      setData(formattedData);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _tiposCaracteres = await getTiposDeCaracteres();
      const _consecutivos = await getPeriodos();
      const _valores = await getCatalogosDeValor();
      if (idConfiguracion) {
        const segmentos = await getSegmentosDeFolios(idConfiguracion);
        const formattedData = segmentos.map(formatter).sort(sorter);
        if (mounted) {
          setSegmentos(formattedData);
          setData(formattedData);
        }
      }
      setLoading(false);
      if (mounted) {
        setTiposCaracteres(_tiposCaracteres);
        setConsecutivos(_consecutivos);
        setValores(_valores);
        setLoading(false);
      }
    };
    fetchAll();

    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setIsConsecutivo(false);
    setFilterdValores([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const clone = [...data];
      if (!idConfiguracion) {
        if (!selectedRowKeys.length) {
          clone.push({
            ...values,
            posicion: clone.length + 1,
            key: clone.length + 1,
            modificable: true,
            deletable: true,
          });
          setData(clone);
          setSegmentos(clone);
          onCancel();
        } else {
          const [key] = selectedRowKeys;
          const res = clone.map((item) => (item.key === key
            ? { ...values, posicion: item.posicion, key } : item));
          setData(res);
          setSegmentos(res);
          onCancel();
        }
      } else {
        const added = await postSegmentoDeFolio({
          ...values,
          posicion: data.length + 1,
          configuracion_de_folio: idConfiguracion,
        });
        if (added) {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = () => {
    const [key] = selectedRowKeys;
    const clone = data.filter((item) => item.key !== key);
    const normalized = clone.map((item, idx) => ({ ...item, id: idx }));
    setData(normalized);
    setSegmentos(normalized);
    onCancel();
    setVisibleAlert(false);
  };

  const handleOnRowClick = (record) => {
    if (record.modificable) {
      setSelectedRowKeys([record.key]);
      form.setFieldsValue(record);
    }
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.key === key);
    if (match && match.deletable) {
      setVisibleAlert(true);
    } else {
      message.info('No se pueden eliminar los segmentos previos');
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Posición',
      dataIndex: 'posicion',
      key: 'posicion',
      width: 100,
    },
    {
      titleText: 'Tipo de caracter',
      dataIndex: 'tipo_de_segmento',
      key: 'tipo_de_segmento',
      width: 200,
      render: (val, record) => {
        let output;
        let valor;
        switch (record.tipo_de_segmento) {
          case 1:
            output = record.folio_siguiente || '';
            break;
          case 2:
            valor = valores.find((t) => t.id === val);
            output = valor?.clave;
            break;
          case 3:
            output = record.fijo;
            break;
          default:
            break;
        }
        return output || 'N/A';
      },
    },
    {
      titleText: 'Longitud',
      dataIndex: 'longitud_del_folio',
      key: 'longitud_del_folio',
      width: 100,
    },
    {
      titleText: 'Valor fijo/siguiente',
      dataIndex: 'catalogo_de_valor',
      key: 'catalogo_de_valor',
      width: 180,
      render: (val, record) => {
        let output;
        let valor;
        switch (record.tipo_de_segmento) {
          case 1:
            output = record.folio_siguiente || '';
            break;
          case 2:
            valor = valores.find((t) => t.id === val);
            output = valor?.clave;
            break;
          case 3:
            output = record.fijo;
            break;
          default:
            break;
        }
        return output || 'N/A';
      },
    },
  ];

  const moveToLocal = (where) => {
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.key === key);
    const filtered = data.filter((item) => item.key !== key);
    switch (where) {
      case 'first':
        filtered.splice(0, 0, match);
        break;
      case 'up':
        filtered.splice(match.posicion - 2, 0, match);
        break;
      case 'down':
        filtered.splice(match.posicion, 0, match);
        break;
      case 'last':
        filtered.splice(data.length, 0, match);
        break;
      default:
        break;
    }
    const final = filtered.map((i, idx) => ({ ...i, posicion: idx + 1 }));
    setData(final);
    setSegmentos(final);
  };

  const moveTo = async (where) => {
    setLoading(true);
    if (idConfiguracion) {
      const [key] = selectedRowKeys;
      const match = data.find((item) => item.key === key);
      let posicion = 0;
      switch (where) {
        case 'first':
          posicion = 1;
          break;
        case 'up':
          posicion = match.posicion - 1;
          break;
        case 'down':
          posicion = match.posicion + 1;
          break;
        case 'last':
          posicion = data.length;
          break;
        default:
          posicion = match.posicion;
          break;
      }
      const res = await patchSegmentosDeFolios(key, { posicion });
      if (res?.status === 200) {
        fetchData();
      }
    } else {
      moveToLocal(where);
    }
    setLoading(false);
  };

  const sortTypes = (arr = tiposCaracteres) => {
    const sorted = arr.map((t) => ({
      ...t,
      disabled: !!data.find((i) => i.tipo_de_segmento_contable === t.id
        && t.descripcion.toLowerCase() !== 'control interno'),
    }));
    setTiposCaracteres(sorted.sort((a, b) => a.disabled.toString().localeCompare(b.disabled)));
  };

  useEffect(() => {
    sortTypes();
    // eslint-disable-next-line
  }, [data]);

  const restProps = isMutable ? {
    rowSelection,
    handleOnRowClick,
  } : {};

  const SortControls = () => (
    <Col className="sorting">
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion <= 2}
        onClick={() => moveTo('first')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion < 2}
        onClick={() => moveTo('up')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion === data.length}
        onClick={() => moveTo('down')}
      />
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion >= data.length - 1}
        onClick={() => moveTo('last')}
      />
    </Col>
  );

  const onChangeValor = (val) => {
    if (val === 1 || val === 2 || val === 4) {
      form.setFieldsValue({ longitud_del_folio: 2 });
    } else if (val === 3) {
      form.setFieldsValue({ longitud_del_folio: 3 });
    } else if (val === 5) {
      form.setFieldsValue({ longitud_del_folio: 4 });
    }
  };

  const onChangeTipoCaracter = (val) => {
    if (val) {
      setIsConsecutivo(val === 1);
      setIsFijo(val === 3);
      setFilterdValores(valores);
    } else {
      setFilterdValores([]);
    }
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.key === key);
    if (match?.modificable) {
      onChangeTipoCaracter(match.tipo_de_segmento);
      setVisible(true);
    } else {
      message.info('No se pueden editar los segmentos previos');
    }
  };

  return (
    <Row id="sort-rows" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row>
          <Col style={{ width: `calc(100% - ${isMutable ? 50 : 0}px)` }}>
            <Table
              cols={columns}
              data={data}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...restProps}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              allowSearch={false}
              allowSort={false}
              allowAdd={allowAdd}
              allowEdit={!idConfiguracion}
              allowDelete={!idConfiguracion}
              permission={permission}
              hasAllViewPermission={hasAllViewPermission}
            />
          </Col>
          {isMutable && <SortControls />}
        </Row>
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Segmento`}
              permission={permission}
            />
          )}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          onCancel={onCancel}
          footer={null}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
            onValuesChange={(changedValues, allValues) => {
              const { fijo, catalogo_de_valor } = changedValues;
              if (fijo) {
                form.setFieldsValue({ longitud_del_folio: fijo.length });
              } else if (catalogo_de_valor && allValues.tipo_de_segmento !== 3) {
                const valor = valores.find((v) => v.id === catalogo_de_valor);
                form.setFieldsValue({ longitud_del_folio: valor.clave.length });
              } else if (fijo === '') {
                form.setFieldsValue({ longitud_del_folio: 0 });
              }
            }}
          >
            <Col span={24}>
              <Form.Item
                name="tipo_de_segmento"
                label="Tipo de caracter"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={tiposCaracteres}
                  onChange={onChangeTipoCaracter}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <NumericInput
                label="Longitud"
                name="longitud_del_folio"
                required
                maxLength={3}
                disabled={form.getFieldValue('tipo_de_segmento') !== 1}
              />
            </Col>
            {(!isConsecutivo && !isFijo && form.getFieldValue('tipo_de_segmento')) && (
              <Col span={24}>
                <Form.Item
                  label="Valor"
                  name="catalogo_de_valor"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select
                    dataSource={filterdValores}
                    keyLabelRender
                    onChange={form.getFieldValue('tipo_de_segmento') === 3
                      ? onChangeValor : null}
                  />
                </Form.Item>
              </Col>
            )}
            {isConsecutivo && (
              <Col span={24}>
                <NumericInput
                  label="Valor inicial"
                  name="folio_siguiente"
                  required
                />
              </Col>
            )}
            {isFijo && (
              <Col span={24}>
                <Form.Item
                  label="Valor fijo"
                  name="fijo"
                  rules={rules.required}
                  hasFeedback
                  normalize={toUpper}
                >
                  <Input maxLength={10} className="uppercase" />
                </Form.Item>
              </Col>
            )}
            {isConsecutivo && (
              <Col span={24}>
                <Form.Item
                  name="catalogo_de_periodo"
                  rules={rules.required}
                  label="Consecutivo"
                  hasFeedback
                >
                  <Select dataSource={consecutivos} />
                </Form.Item>
              </Col>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content={`Segmento en posición ${form.getFieldValue('posicion')}`}
        loading={loading}
      />
    </Row>
  );
};

SegmentosFoliador.propTypes = {
  idConfiguracion: PropTypes.number,
  isMutable: PropTypes.bool.isRequired,
  setSegmentos: PropTypes.func.isRequired,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
  hasAllViewPermission: PropTypes.bool,
};

SegmentosFoliador.defaultProps = {
  idConfiguracion: null,
  hasAllViewPermission: false,
};

export default SegmentosFoliador;
